/* eslint-disable no-console */
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, Container, Grid, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createCompanyGroupFormFields } from '../../../forms/fields/formFields/createCompanyGroup';
import { createCompanyGroupSchema } from '../../../forms/validationSchema/formSchema/createCompanyGroup';
import { useAppDispatch } from '../../../redux/hooks';
import {
  createCompanyGroup,
  fetchCompanyGroup,
  fetchPortfolios,
  updateCompanyGroup,
} from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.actions';
import {
  companyGroupCreateLoadingSelector,
  companyGroupLoadingSelector,
  companyGroupSelector,
  companyGroupUpdateLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.selectors';
import { PortfolioCards } from './components/PortfolioCards';

export const CreateCompanyGroup = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { companyId, action } = useParams<{ companyId: string; action: string }>();
  const isCreateLoading = useSelector(companyGroupCreateLoadingSelector);
  const isUpdateLoading = useSelector(companyGroupUpdateLoadingSelector);
  const isFetchLoading = useSelector(companyGroupLoadingSelector);
  const { selectedCompanyGroup } = useSelector(companyGroupSelector);
  const isEdit = action !== 'create';
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  const { portfolios } = useSelector(companyGroupSelector);

  const { handleSubmit, values, handleChange, setFieldValue, touched, errors, resetForm, isValid, dirty } = useFormik({
    initialValues: {
      [createCompanyGroupFormFields.name]: '',
      [createCompanyGroupFormFields.contribution]: 0,
      [createCompanyGroupFormFields.maxCap]: 0,
      [createCompanyGroupFormFields.lockupPeriod]: 0,
      [createCompanyGroupFormFields.coContributionDueDate]: '',
      [createCompanyGroupFormFields.numberOfWithdrawalsAllowed]: 0,
      [createCompanyGroupFormFields.externalPortfolioIds]: [] as number[],
    },
    validationSchema: createCompanyGroupSchema,
    validateOnMount: true,
    onSubmit: async (formValues) => {
      if (!companyId) {
        enqueueSnackbar('Company ID is required', { variant: 'error' });
        return;
      }

      try {
        if (isEdit && action) {
          // Get only changed fields
          const changedFields: Record<string, any> = {};
          Object.keys(formValues).forEach((key) => {
            const currentValue = formValues[key];
            const initialValue = initialValues[key];
            // Normalize values for comparison (handle empty strings, numbers, and null/undefined)
            let normalizedCurrent: any = currentValue;
            if (typeof currentValue === 'number') {
              normalizedCurrent = currentValue;
            } else if (currentValue === '') {
              normalizedCurrent = null;
            }
            let normalizedInitial: any = initialValue;
            if (typeof initialValue === 'number') {
              normalizedInitial = initialValue;
            } else if (initialValue === '') {
              normalizedInitial = null;
            }
            if (normalizedCurrent !== normalizedInitial) {
              changedFields[key] = currentValue;
            }
          });

          // Always send full selected portfolio ids on edit
          changedFields[createCompanyGroupFormFields.externalPortfolioIds] =
            formValues[createCompanyGroupFormFields.externalPortfolioIds];

          await dispatch(
            updateCompanyGroup({
              companyId: Number(companyId),
              groupId: Number(action),
              payload: changedFields as any,
            }),
          ).unwrap();
          enqueueSnackbar('Company group updated!', { variant: 'success' });
        } else {
          await dispatch(
            createCompanyGroup({
              companyId: Number(companyId),
              payload: {
                name: formValues[createCompanyGroupFormFields.name],
                contribution: formValues[createCompanyGroupFormFields.contribution],
                maxCap: formValues[createCompanyGroupFormFields.maxCap],
                lockupPeriod: formValues[createCompanyGroupFormFields.lockupPeriod],
                coContributionDueDate: formValues[createCompanyGroupFormFields.coContributionDueDate],
                numberOfWithdrawalsAllowed: formValues[createCompanyGroupFormFields.numberOfWithdrawalsAllowed],
                externalPortfolioIds: formValues[createCompanyGroupFormFields.externalPortfolioIds],
              },
            }),
          ).unwrap();
          enqueueSnackbar('Company group created!', { variant: 'success' });
        }
        navigate(`/companies/${companyId}/groups`);
      } catch (error: any) {
        const errorMessage =
          error?.message || (isEdit ? 'Company group update error!' : 'Company group creation error!');
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
  });

  useEffect(() => {
    if (isEdit && action && companyId) {
      dispatch(fetchCompanyGroup({ companyId: Number(companyId), groupId: Number(action) }));
    }
  }, [isEdit, action, companyId, dispatch]);

  useEffect(() => {
    if (isEdit && selectedCompanyGroup) {
      const initialFormValues = {
        [createCompanyGroupFormFields.name]: selectedCompanyGroup.name || '',
        [createCompanyGroupFormFields.contribution]: Number(selectedCompanyGroup.contribution) || 0,
        [createCompanyGroupFormFields.maxCap]: Number(selectedCompanyGroup.maxCap) || 0,
        [createCompanyGroupFormFields.lockupPeriod]: selectedCompanyGroup.lockupPeriod || 0,
        [createCompanyGroupFormFields.coContributionDueDate]: selectedCompanyGroup.coContributionDueDate || '',
        [createCompanyGroupFormFields.numberOfWithdrawalsAllowed]: selectedCompanyGroup.numberOfWithdrawalsAllowed || 0,
        // Initialize the selected portfolio from response (external_portfolio_id)
        [createCompanyGroupFormFields.externalPortfolioIds]: (
          (selectedCompanyGroup as any)?.portfolioGroups
            ?.map((pg: any) => Number(pg?.external_portfolio_id))
            .filter((id: any) => Number.isFinite(id)) || []
        ).slice(0, 1),
      };
      setInitialValues(initialFormValues);
      resetForm({
        values: initialFormValues,
      });
    } else {
      setInitialValues({});
    }
  }, [isEdit, selectedCompanyGroup, resetForm]);

  const selectedPortfolioId = useMemo(
    () => (values[createCompanyGroupFormFields.externalPortfolioIds] as number[])[0],
    [values],
  );

  useEffect(() => {
    dispatch(fetchPortfolios({}));
  }, [dispatch]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">{isEdit ? 'Edit Company Group' : 'Create Company Group'}</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyGroupFormFields.name}
              name={createCompanyGroupFormFields.name}
              label="Name"
              value={values[createCompanyGroupFormFields.name]}
              onChange={handleChange}
              error={touched.name && !!errors.name}
              helperText={touched.name && errors.name}
            />
          </Grid>

          {/* Contribution */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyGroupFormFields.contribution}
              name={createCompanyGroupFormFields.contribution}
              label="Contribution (%)"
              type="number"
              value={values[createCompanyGroupFormFields.contribution]}
              onChange={handleChange}
              error={touched.contribution && !!errors.contribution}
              helperText={touched.contribution && errors.contribution}
            />
          </Grid>

          {/* Max Cap */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyGroupFormFields.maxCap}
              name={createCompanyGroupFormFields.maxCap}
              label="Max Cap"
              type="number"
              value={values[createCompanyGroupFormFields.maxCap]}
              onChange={handleChange}
              error={touched.maxCap && !!errors.maxCap}
              helperText={touched.maxCap && errors.maxCap}
            />
          </Grid>

          {/* Lockup Period */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyGroupFormFields.lockupPeriod}
              name={createCompanyGroupFormFields.lockupPeriod}
              label="Lockup Period (Days)"
              type="number"
              value={values[createCompanyGroupFormFields.lockupPeriod]}
              onChange={handleChange}
              error={touched.lockupPeriod && !!errors.lockupPeriod}
              helperText={touched.lockupPeriod && errors.lockupPeriod}
            />
          </Grid>

          {/* Co-Contribution Due Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyGroupFormFields.coContributionDueDate}
              name={createCompanyGroupFormFields.coContributionDueDate}
              label="Co-Contribution Due Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={values[createCompanyGroupFormFields.coContributionDueDate]}
              onChange={handleChange}
              error={touched.coContributionDueDate && !!errors.coContributionDueDate}
              helperText={touched.coContributionDueDate && errors.coContributionDueDate}
            />
          </Grid>

          {/* Number of Withdrawals Allowed */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id={createCompanyGroupFormFields.numberOfWithdrawalsAllowed}
              name={createCompanyGroupFormFields.numberOfWithdrawalsAllowed}
              label="Number of Withdrawals Allowed"
              type="number"
              value={values[createCompanyGroupFormFields.numberOfWithdrawalsAllowed]}
              onChange={handleChange}
              error={touched.numberOfWithdrawalsAllowed && !!errors.numberOfWithdrawalsAllowed}
              helperText={touched.numberOfWithdrawalsAllowed && errors.numberOfWithdrawalsAllowed}
            />
          </Grid>

          {/* Portfolios Selection */}
          <Grid item xs={12}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="h6">Select Preference *</Typography>
              <Typography variant="body2" color="text.secondary">
                Add one product to group
              </Typography>
            </Box>
            <PortfolioCards
              portfolios={portfolios ?? []}
              selectedPortfolioId={selectedPortfolioId}
              onSelect={(portfolioId) =>
                setFieldValue(createCompanyGroupFormFields.externalPortfolioIds, [portfolioId])
              }
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isCreateLoading || isUpdateLoading || isFetchLoading}
                startIcon={<SaveIcon />}
                disabled={!isValid || !dirty || isCreateLoading || isUpdateLoading || isFetchLoading}
              >
                {isEdit ? 'Update Group' : 'Create Group'}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};
